import firebase from 'firebase/app'
import 'firebase/database'
import config from '@/config/config'
import 'firebase/functions'
import { functions } from '@/firebase'

const db = firebase.database()

const snapshotParser = (snapshotsObject) => {
  if (snapshotsObject && Object.keys(snapshotsObject).length !== 0) {
    return Object.keys(snapshotsObject).map(x => {
      return {
        ...snapshotsObject[x],
        id: x
      }
    })
  }
  return []
}

export default {
  namespaced: true,
  state: {
    usersQRCodes: {},
    qrCodeVersions: {},
    loadedQrCode: {},
    qrScans: {},
    qrStats: {},
    allQrCodes: {}
  },
  getters: {
    projectFonts (state) {
      return state.projectFonts
    },
    getUsersQRCodes (state) {
      return state.usersQRCodes
    },
    getLoadedQrCode (state) {
      return state.loadedQrCode
    },
    getUsersQRCodesArray (state) {
      return snapshotParser(state.usersQRCodes).reverse()
    },
    getAllQRCodesArray (state) {
      return snapshotParser(state.allQrCodes)
    },
    getVersionsArray (state) {
      return snapshotParser(state.qrCodeVersions)
    },
    getQrScansArray (state) {
      return snapshotParser(state.qrScans)
    },
    getQrStats (state) {
      if (state.qrStats) {
        return state.qrStats
      }
      return null
    }
  },
  mutations: {
    setAllQRCodes (state, codes) {
      state.allQrCodes = codes
    },
    setUsersQRCodes (state, codes) {
      state.usersQRCodes = codes
    },
    setQrCodeVersions (state, versions) {
      state.qrCodeVersions = versions
    },
    setLoadedQrCode (state, loadedQrCode) {
      state.loadedQrCode = loadedQrCode
    },
    setQrScans (state, qrScans) {
      state.qrScans = qrScans
    },
    setQrStats (state, qrStats) {
      state.qrStats = qrStats
    }
  },
  actions: {
    async loadQrData ({ commit }, pushId) {
      const qrCodeRef = db.ref(`qr_codes/${pushId}`)
      const codeSnapshot = await qrCodeRef.once('value')
      commit('setLoadedQrCode', codeSnapshot.val())
      return codeSnapshot.val()
    },
    async getQrCodePushId ({  }) {
      const qrCodesRef = db.ref('qr_codes')
      const qrCodePushId = await qrCodesRef.push().getKey()
      return qrCodePushId
    },
    async loadQrCodeVersions ({ commit }, qrUid) {
      const qrVersionRef = db.ref('/qr_code_versions').orderByChild('qrCodeUid').equalTo(qrUid)
      const qrVersionsSnap = await qrVersionRef.once('value')
      const qrVersions = qrVersionsSnap.val()
      commit('setQrCodeVersions', qrVersions)
    },

    async saveQrCodeVersion ({ }, { codeData, qrCodeUid }) {
      const createdVersionRef = await db.ref('/qr_code_versions').push({
        ...codeData,
        qrCodeUid
      })

      return createdVersionRef.key
    },
    async sendNotificationToSlack ({ rootState }, { qrCodeUid, codeData }) {
      const user = rootState.auth.user
      const lpDomain = (config.LP_DOMAINS && config.LP_DOMAINS[0]) || ''

      await functions.httpsCallable('qrCodes-sendQRNotificationToSlack')({
        zapierWebhook: config.QR_CREATED_WEBHOOK,
        uid: user.uid,
        username: user.username,
        email: user.email,
        qrName: codeData.name,
        qrLink: codeData.redirectToUrl,
        qrHashLink: 'https://' + lpDomain + '/qr/' + codeData.endpointHash,
        qrCodeUid: qrCodeUid,
        creationTime: new Date(),
        userSignUpDate: user && user.metadata && user.metadata.creationTime,
        userSource: (user && user.source) || 'none'
      })

      return 'sent'
    },

    async uploadQrCodeImage ({ }, { blob, pushId }) {
      const fileName = Date.now() + '_' + 'qr_code'
      const storageRef = firebase.storage().ref(`qr_codes/${pushId}/${fileName}`)
      const uploadTask = storageRef.put(blob)

      return new Promise((resolve, reject) => {
        uploadTask.on('state_changed',
          (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100
            console.log('Upload is ' + progress + '% done')
          },
          (err) => reject(err),
          () => {
            uploadTask.snapshot.ref.getDownloadURL().then((downloadURL) => {
              resolve(downloadURL)
            })
          }
        )
      })
    },

    async saveQrCodeImages ({  dispatch }, { pushId, png, svg }) {
      const svgLink = await dispatch('uploadQrCodeImage', { blob: svg, pushId, type: 'svg' })
      const pngLink = await dispatch('uploadQrCodeImage', { blob: png, pushId, type: 'png' })
      return {
        png: pngLink,
        svg: svgLink
      }
    },

    async saveQRCode ({ rootState, dispatch }, { codePayload, qrCodeUid }) {
      const userId = rootState.auth.user && rootState.auth.user.uid
      const userEmail = rootState.auth.user && rootState.auth.user.email
      const userName = (rootState.auth.user && rootState.auth.user.username) || ''

      const qrCodeRef = db.ref(`qr_codes/${qrCodeUid}`)
      const codeData = { ...codePayload, userUid: userId, userName: userName, userEmail: userEmail }

      const versionUid = await dispatch('saveQrCodeVersion', { codeData, qrCodeUid })
      codeData.currentVersionUid = versionUid
      await qrCodeRef.set(codeData)
      await dispatch('sendNotificationToSlack', { qrCodeUid, codeData })
      return 'QR code saved'
    },

    async updateQRCode ({ rootState, dispatch }, { codePayload, qrCodeUid }) {
      const userId = rootState.auth.user && rootState.auth.user.uid
      const qrCodeRef = db.ref(`qr_codes/${qrCodeUid}`)

      // create QR Code version
      const versionData = {
        ...codePayload,
        userUid: userId,
        createdAt: codePayload.updatedAt,
        updatedAt: null
      }

      const versionUid = await dispatch('saveQrCodeVersion', { codeData: versionData, qrCodeUid })
      codePayload.currentVersionUid = versionUid

      await qrCodeRef.update({
        ...codePayload
      })
      await dispatch('sendNotificationToSlack', { qrCodeUid, codeData: codePayload })

      return 'updated'
    },
    async getQRCodesFoId ({ commit }, userId) {
      const qrCodesRef = db.ref('qr_codes').orderByChild('userUid').equalTo(userId)
      const qrCodesSnap = await qrCodesRef.once('value')
      const qrCodes = qrCodesSnap.val()
      commit('setUsersQRCodes', qrCodes)
      return qrCodes
    },
    async getQRCodes ({ rootState, commit }) {
      const user = rootState.auth.user
      const qrCodesRef = db.ref('qr_codes').orderByChild('userUid').equalTo(user.uid)
      const qrCodesSnap = await qrCodesRef.once('value')
      const qrCodes = qrCodesSnap.val()

      if (user.enterprise && user.enterprise.id) {
        await db.ref('qr_codes')
          .orderByChild('sharedForEnterprise')
          .equalTo(user.enterprise.id)
          .on('value', snapshot => {
            const sharedQRs = snapshot.val()
            commit('setUsersQRCodes', { ...qrCodes, ...sharedQRs })
          })
        return
      }

      commit('setUsersQRCodes', qrCodes)
      return qrCodes
    },
    // admin call
    async getAllQRCodes ({ commit }) {
      const qrCodesRef = db.ref('qr_codes')
      const qrCodesSnap = await qrCodesRef.once('value')
      const qrCodes = qrCodesSnap.val()
      commit('setAllQRCodes', qrCodes)
      return qrCodes
    },
    async deleteQrCode ({ dispatch }, codeId) {
      const codeRef = db.ref(`qr_codes/${codeId}`)
      codeRef.remove()
      dispatch('getQRCodes')
    },
    //
    // QR Scans
    //
    async loadQrScans ({ commit }, qrUid) {
      const qrScansRef = db.ref('qr_code_scans').orderByChild('qrUid').equalTo(qrUid)
      const qrScansSnap = await qrScansRef.once('value')
      const qrScans = qrScansSnap.val()

      commit('setQrScans', qrScans)
      return qrScans
    },
    async loadQrStats ({ commit }, qrUid) {
      const qrStatsRef = db.ref(`qr_code_stats/${qrUid}`)
      const qrStatsSnap = await qrStatsRef.once('value')
      const qrStats = qrStatsSnap.val()

      commit('setQrStats', qrStats)
      return qrStats
    },

    async convertLogoToSvg ({ }, { url, color, background, convertToBitmap }) {
      const result = await functions.httpsCallable('qrCodes-convertLogoToSvg')({
        image: url,
        color,
        background,
        convertToBitmap
      })
      return result
    },

    async loadQrAggregatedStats ({ }, { qrUid, qrVersion }) {
      const firestore = firebase.firestore()

      let snapshot
      if (!qrVersion) {
        snapshot = await firestore.collection('scans')
          .where('qrUid', '==', qrUid)
          .get()
      } else {
        snapshot = await firestore.collection('scans')
          .where('qrUid', '==', qrUid)
          .where('qrVersionUid', '==', qrVersion)
          .get()
      }

      if (!snapshot || snapshot.empty) return {}
      const scans = {}

      snapshot.forEach((doc) => {
        scans[doc.id] = doc.data()
      })
      return snapshotParser(scans)
    },
    // sharing and handover
    async shareQRCode ({}, { qrUid, enterpriseUid }) {
      const result = await functions.httpsCallable('qrCodes-shareQRCode')({
        qrCodeUid: qrUid,
        enterpriseUid
      })
      return result
    },
    async shareAllUsersQRs ({}, { userUid, enterpriseUid }) {
      return await functions.httpsCallable('qrCodes-shareQRCodesAmongEnterpriseUsers')({
        userUid,
        enterpriseUid
      })
    },
    async handoverQR ({}, { qrUid, destinationUserUid }) {
      return await functions.httpsCallable('qrCodes-handOverQRCode')({
        qrCodeId: qrUid,
        handoverToAccountId: destinationUserUid
      })
    },
    async handoverAllUsersQRs ({}, { sourceUserUid, destinationUserUid }) {
      return await functions.httpsCallable('qrCodes-handOverAllQRCodes')({
        originalOwnerUid: sourceUserUid,
        newOwnerUid: destinationUserUid
      })
    }
  }
}
